import React from 'react';
import classnames from 'classnames';
import { withStyles } from 'theme/withStyles';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import useEmbedPdf from 'hooks/useEmbedPdf';
import { HOME_ROOT } from 'config/api';
import dialogActions from 'core/dialogs/actions';
import Button from 'common/Button';
import Spinner from 'common/Spinner';
import useScrolledToBottomOfPdf from 'hooks/useScrolledToBottomOfPdf';

import dialogStyles from './styles/dialog';
import contentStyles from './styles/content';
import bodyStyles from './styles/body';
import footerStyles from './styles/footer';
import noticeStyles from './styles/notice';
import embedPdf from './styles/embedPdf';

const styles = (theme) => {
  const common = {
    body: bodyStyles(theme),
    content: contentStyles(theme),
    dialog: dialogStyles(theme),
    embedPdf: embedPdf(theme),
    footer: footerStyles(theme),
    notice: noticeStyles(theme),
  };
  return {
    ...common,
    content: {
      ...common.content,
      borderLeft: 'none',
      display: 'flex',
    },
    dialog: {
      ...common.dialog,
      height: '90%',
      maxWidth: '100%',
      width: '90%',

      [`@media(max-width: ${theme.screenSizes.mobileMax}px)`]: {
        height: '100%',
        width: '100%',
      },
    },
  };
};

export const HomeDisclosuresBare = ({
  agree,
  classes,
  close,
  commonClasses,
  disclosure,
  disclosureAcceptLabel,
  disclosureCancelLabel,
  disclosureCloseLabel,
  loading,
  request,
}) => {
  useEmbedPdf(`${HOME_ROOT}/${request.id}/disclosures/${disclosure.id}`, '#embed-pdf', {
    pdfOpenParams: {
      view: 'FitH',
    },
    title: disclosure.label,
  });
  const isScrolledBottom = useScrolledToBottomOfPdf('#embed-pdf');
  const buttons =
    disclosure.status === 'PENDING' && request.pendingDisclosures
      ? [
          {
            className: classnames('btn btn-mobile btn-i-accept', { loading }),
            disabled: !isScrolledBottom || loading,
            onClick: () => {
              agree(request.id, disclosure.id);
            },
            text: disclosureAcceptLabel,
            variant: 'primary',
          },
          {
            className: 'btn btn-mobile btn-mobile--secondary btn-i-decline',
            disabled: loading,
            onClick: close,
            text: disclosureCancelLabel,
            variant: 'secondary',
          },
        ]
      : [
          {
            className: 'btn btn-mobile btn-i-accept',
            onClick: close,
            text: disclosureCloseLabel,
            variant: 'primary',
          },
        ];

  return (
    <div className={classnames('modal-pdf', commonClasses, classes.dialog)}>
      <div className={classnames('modal-content', classes.content)}>
        <div className={classnames('modal-body', classes.body)} id="modal-body">
          <div>
            <div id="embed-pdf" className={classnames('embed-pdf', classes.embedPdf)} />
          </div>
        </div>
        <div className={classnames('modal-footer', classes.footer)}>
          {buttons.map((b, i) => (
            <Button
              /* eslint-disable-next-line react/no-array-index-key */
              key={`dialog-button-${i}`}
              data-key={i}
              {...b}
            >
              {b.text}
              {b.className.includes('btn-i-accept') && (
                <>
                  <i className="icon-arrow-thin-right" aria-hidden="true" />
                  <Spinner loading={loading} />
                </>
              )}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};

HomeDisclosuresBare.propTypes = {
  agree: PropTypes.func.isRequired,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  close: PropTypes.func.isRequired,
  commonClasses: PropTypes.string.isRequired,
  disclosure: PropTypes.shape({
    id: PropTypes.number,
    label: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
  disclosureAcceptLabel: PropTypes.string,
  disclosureCancelLabel: PropTypes.string,
  disclosureCloseLabel: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  request: PropTypes.shape({
    id: PropTypes.number,
    pendingDisclosures: PropTypes.bool,
  }).isRequired,
};

HomeDisclosuresBare.defaultProps = {
  disclosureAcceptLabel: 'Accept',
  disclosureCancelLabel: 'Cancel',
  disclosureCloseLabel: 'Close',
};

const mapStateToProps = ({ dialogs, resources }) => ({
  disclosure: dialogs.params.disclosure,
  disclosureAcceptLabel: resources.disclosureAcceptLabel,
  disclosureCancelLabel: resources.disclosureCancelLabel,
  disclosureCloseLabel: resources.disclosureCloseLabel,
  loading: dialogs.params.loading || false,
  request: dialogs.params.request,
});

const mapDispatchToProps = {
  agree: dialogActions.agree,
  close: dialogActions.close,
};

const HomeDisclosuresStyled = withStyles(styles)(HomeDisclosuresBare);

export default connect(mapStateToProps, mapDispatchToProps)(HomeDisclosuresStyled);
